<template>
  <div class="step-card">
    <div class="step-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">What's your role?</h2>
        <p class="step-subtitle">Help us personalize your experience</p>
      </div>

      <!-- Role Buttons Grid -->
      <div class="role-buttons-grid">
        <button
          class="role-button"
          :class="{ active: selectedRole === 'Teacher' }"
          :aria-pressed="selectedRole === 'Teacher'"
          @click="handleRoleSelect('Teacher')"
        >
          <div class="role-button-content">
            <div class="role-icon-container">
              <div class="role-icon">
                <svg
                  width="27"
                  height="25"
                  viewBox="0 0 27 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.3333 14.6666L25.3333 7.99992L13.3333 1.33325L1.33325 7.99992L13.3333 14.6666ZM13.3333 14.6666L21.5454 10.1043C22.2689 11.9287 22.6666 13.9178 22.6666 15.9998C22.6666 16.9349 22.5864 17.8513 22.4324 18.7424C18.9523 19.0808 15.7986 20.5341 13.3333 22.7406C10.8679 20.5341 7.7142 19.0808 4.23408 18.7424C4.08014 17.8513 3.99992 16.9349 3.99992 15.9998C3.99992 13.9178 4.39758 11.9287 5.12109 10.1042L13.3333 14.6666ZM7.99992 22.6665V12.6666L13.3333 9.70362"
                    stroke="currentColor"
                    stroke-width="2.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div class="role-text-container">
              <p class="role-name">Teacher</p>
              <p class="role-description">I teach students in a classroom</p>
            </div>
          </div>
        </button>

        <button
          class="role-button"
          :class="{ active: selectedRole === 'Trainer' }"
          :aria-pressed="selectedRole === 'Trainer'"
          @click="handleRoleSelect('Trainer')"
        >
          <div class="role-button-content">
            <div class="role-icon-container">
              <div class="role-icon">
                <svg
                  width="30"
                  height="24"
                  viewBox="0 0 30 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21.3333 22.6666H27.9999V19.9999C27.9999 17.7908 26.2091 15.9999 23.9999 15.9999C22.7258 15.9999 21.5908 16.5957 20.8583 17.5238M21.3333 22.6666H7.99992M21.3333 22.6666V19.9999C21.3333 19.1249 21.1647 18.2893 20.8583 17.5238M7.99992 22.6666H1.33325V19.9999C1.33325 17.7908 3.12411 15.9999 5.33325 15.9999C6.60739 15.9999 7.7424 16.5957 8.47491 17.5238M7.99992 22.6666V19.9999C7.99992 19.1249 8.16848 18.2893 8.47491 17.5238M8.47491 17.5238C9.45792 15.068 11.8597 13.3333 14.6666 13.3333C17.4735 13.3333 19.8752 15.068 20.8583 17.5238M18.6666 5.33325C18.6666 7.54239 16.8757 9.33325 14.6666 9.33325C12.4574 9.33325 10.6666 7.54239 10.6666 5.33325C10.6666 3.12411 12.4574 1.33325 14.6666 1.33325C16.8757 1.33325 18.6666 3.12411 18.6666 5.33325ZM26.6666 9.33325C26.6666 10.806 25.4727 11.9999 23.9999 11.9999C22.5272 11.9999 21.3333 10.806 21.3333 9.33325C21.3333 7.86049 22.5272 6.66659 23.9999 6.66659C25.4727 6.66659 26.6666 7.86049 26.6666 9.33325ZM7.99992 9.33325C7.99992 10.806 6.80601 11.9999 5.33325 11.9999C3.86049 11.9999 2.66659 10.806 2.66659 9.33325C2.66659 7.86049 3.86049 6.66659 5.33325 6.66659C6.80601 6.66659 7.99992 7.86049 7.99992 9.33325Z"
                    stroke="currentColor"
                    stroke-width="2.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div class="role-text-container">
              <p class="role-name">Trainer</p>
              <p class="role-description">
                I train educators and professionals
              </p>
            </div>
          </div>
        </button>
      </div>

      <!-- Next Button -->
      <!-- <div class="button-container">
        <button class="next-button" :class="{ disabled: !selectedRole }" :disabled="!selectedRole" @click="handleNext">
          Next
        </button>
      </div> -->
      <ActionButton
        :disabled="!selectedRole"
        @click="handleNext"
        class="next-button"
        >Next
      </ActionButton>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch } from "vue";
import ActionButton from "../common/ActionButton.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["next", "update:modelValue"]);

const selectedRole = ref(props.modelValue);

// Watch for prop changes (when navigating back)
watch(
  () => props.modelValue,
  (newValue) => {
    if (selectedRole.value !== newValue) {
      selectedRole.value = newValue;
    }
  }
);

const handleRoleSelect = (role) => {
  selectedRole.value = role;
  emit("update:modelValue", role);
};

const handleNext = () => {
  if (selectedRole.value) {
    emit("next", { role: selectedRole.value });
  }
};
</script>

<style scoped>
.step-card {
  background-color: white;
  border: 1px solid #0478ce;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 450px;
  align-items: flex-start;
  padding: 32px;
  border-radius: 16px;
  width: 100%;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-end;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: #3b4648;
}

.step-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.step-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

/* Role Buttons Grid */
.role-buttons-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  padding: 24px 0;
  width: 100%;
}

.role-button {
  background-color: white;
  border: 1px solid #d8dae5;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 26px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.role-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(4, 120, 206, 0.15);
}

.role-button.active {
  background-color: #e6ecf0;
  color: var(--neutral-900);
}

.role-button-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.role-icon-container {
  flex: 1 1 0;
  background-color: #f2f3fb;
  border-radius: 999999px;
  min-width: 0;
  width: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.role-button.active .role-icon-container {
  background-color: var(--primary-blue);
  color: white;
}

.role-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-text-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  text-align: center;
  height: 48px;
}

.role-name {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
  margin: 0;
  min-width: 100%;
}

.role-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #6c737f;
  white-space: nowrap;
  letter-spacing: 0.014px;
  margin: 0;
}

/* Button Container */
.button-container {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.next-button {
  width: 100px;
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

/* Responsive Design */
@media (max-width: 768px) {
  .role-buttons-grid {
    grid-template-columns: 1fr;
  }

  .step-card {
    min-height: auto;
  }

  .next-button {
    width: 100%;
  }
}
</style>
